import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Mail,
  Copy,
  Check,
  Send,
  FileText,
  RefreshCw,
  ChevronRight,
  Clock,
  Heart,
  FolderKanban,
  ShieldCheck,
  Search,
  Sparkles,
  Library
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import EmailComposer from "./EmailComposer";
import CustomTemplates from "./CustomTemplates";

const CATEGORY_INFO = {
  follow_up: { label: "Follow-ups", icon: Clock, color: "text-indigo-400 bg-blue-500/20" },
  thank_you: { label: "Thank You", icon: Heart, color: "text-indigo-400 bg-indigo-500/20" },
  project: { label: "Project", icon: FolderKanban, color: "text-indigo-400 bg-indigo-500/20" },
  boundaries: { label: "Boundaries", icon: ShieldCheck, color: "text-orange-400 bg-orange-500/20" }
};

export default function EmailTemplates() {
  const [activeTab, setActiveTab] = useState("library"); // "library" or "custom"
  const [templates, setTemplates] = useState({});
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [filledContent, setFilledContent] = useState({ subject: "", body: "" });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchTemplates();
    fetchClients();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/email-templates");
      setTemplates(res.data.templates || {});
      setCategories(res.data.categories || {});
    } catch (e) {
      console.error("Error fetching templates:", e);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get("/clients");
      setClients(res.data || []);
    } catch (e) {
      console.error("Error fetching clients:", e);
    }
  };

  const selectTemplate = (key, template) => {
    setSelectedTemplate({ key, ...template });
    setFilledContent({
      subject: template.subject,
      body: template.body
    });
    
    // Extract placeholders and initialize variables
    const placeholders = extractPlaceholders(template.body + template.subject);
    const initialVars = {};
    placeholders.forEach(p => { initialVars[p] = ""; });
    setVariables(initialVars);
  };

  const extractPlaceholders = (text) => {
    const matches = text.match(/\{([^}]+)\}/g) || [];
    return [...new Set(matches.map(m => m.slice(1, -1)))];
  };

  const handleVariableChange = (key, value) => {
    const newVars = { ...variables, [key]: value };
    setVariables(newVars);
    
    // Update filled content
    let newSubject = selectedTemplate.subject;
    let newBody = selectedTemplate.body;
    
    Object.entries(newVars).forEach(([k, v]) => {
      const placeholder = new RegExp(`\\{${k}\\}`, 'g');
      newSubject = newSubject.replace(placeholder, v || `{${k}}`);
      newBody = newBody.replace(placeholder, v || `{${k}}`);
    });
    
    setFilledContent({ subject: newSubject, body: newBody });
  };

  const handleCopy = async () => {
    const fullEmail = `Subject: ${filledContent.subject}\n\n${filledContent.body}`;
    await navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenComposer = () => {
    setShowEmailComposer(true);
  };

  const filteredTemplates = Object.entries(templates).filter(([key, template]) => {
    if (selectedCategory && template.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        template.title.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query) ||
        key.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading && activeTab === "library") {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-templates">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
            <Mail className="w-7 h-7 text-indigo-400" />
            Email Templates
          </h1>
          <p className="text-indigo-400">Pre-written templates and your custom creations</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 p-1 bg-slate-800/50 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("library")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "library"
              ? "bg-indigo-500 text-white"
              : "text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50"
          }`}
          data-testid="library-tab"
        >
          <Library className="w-4 h-4" />
          Template Library
          <Badge className="bg-slate-400/50 text-slate-400 text-xs ml-1">
            {Object.keys(templates).length}
          </Badge>
        </button>
        <button
          onClick={() => setActiveTab("custom")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "custom"
              ? "bg-indigo-500 text-white"
              : "text-indigo-400 hover:text-indigo-500 hover:bg-slate-400/50"
          }`}
          data-testid="custom-tab"
        >
          <Sparkles className="w-4 h-4" />
          My Templates
        </button>
      </div>

      {/* Custom Templates Tab */}
      {activeTab === "custom" && <CustomTemplates />}

      {/* Library Tab */}
      {activeTab === "library" && (
        <>
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                !selectedCategory
                  ? "bg-indigo-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-400"
              }`}
            >
              All Templates
            </button>
            {Object.entries(CATEGORY_INFO).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                    selectedCategory === key
                      ? "bg-indigo-500 text-white"
                      : "bg-slate-800 text-slate-400 hover:bg-slate-400"
                  }`}
                  data-testid={`category-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  {info.label}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-indigo-500 focus:border-indigo-500 focus:outline-none"
              data-testid="search-templates"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Template List */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-indigo-400">
                {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""}
              </h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                {filteredTemplates.map(([key, template]) => {
                  const catInfo = CATEGORY_INFO[template.category] || {};
                  const Icon = catInfo.icon || FileText;
                  const isSelected = selectedTemplate?.key === key;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => selectTemplate(key, template)}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        isSelected
                          ? "bg-indigo-500/20 border-2 border-indigo-500/50"
                          : "bg-slate-800/50 border border-slate-700/50 hover:border-slate-600"
                      }`}
                      data-testid={`template-${key}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${catInfo.color || "bg-slate-400 text-indigo-400"}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-indigo-500 truncate">{template.title}</h4>
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${
                              isSelected ? "rotate-90" : ""
                            }`} />
                          </div>
                          <p className="text-sm text-indigo-400 truncate">{template.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Template Preview / Editor */}
            <div>
              {selectedTemplate ? (
                <Card className="bg-slate-800/50 border-slate-700/50 sticky top-4">
                  <CardHeader>
                    <CardTitle className="text-indigo-500">{selectedTemplate.title}</CardTitle>
                    <CardDescription className="text-indigo-400">
                      {selectedTemplate.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Variables */}
                    {Object.keys(variables).length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-400">Fill in the blanks</h4>
                        <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                          {Object.keys(variables).map((key) => (
                            <div key={key}>
                              <label className="block text-xs text-slate-400 mb-1">
                                {key.replace(/_/g, " ")}
                              </label>
                              <input
                                type="text"
                                value={variables[key]}
                                onChange={(e) => handleVariableChange(key, e.target.value)}
                                placeholder={key.replace(/_/g, " ")}
                                className="w-full px-2 py-1.5 text-sm bg-slate-900/50 border border-slate-600 rounded text-indigo-500 focus:border-indigo-500 focus:outline-none"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-slate-400">Preview</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCopy}
                            className="p-2 bg-slate-400 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Copy to clipboard"
                          >
                            {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-indigo-400" />}
                          </button>
                          <button
                            onClick={handleOpenComposer}
                            className="flex items-center gap-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-indigo-500 rounded-lg transition-colors text-sm"
                            data-testid="send-template-btn"
                          >
                            <Send className="w-4 h-4" />
                            Send
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                        <p className="text-sm text-indigo-400 mb-2">
                          <span className="text-slate-400">Subject:</span>{" "}
                          <span className="text-indigo-500">{filledContent.subject}</span>
                        </p>
                        <div className="border-t border-slate-700 pt-3">
                          <pre className="whitespace-pre-wrap text-sm text-slate-600 font-sans">
                            {filledContent.body}
                          </pre>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="py-12 text-center">
                    <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-400 mb-2">Select a template</h3>
                    <p className="text-slate-400">
                      Choose a template from the list to preview and customize
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={`Subject: ${filledContent.subject}\n\n${filledContent.body}`}
        clients={clients}
      />
    </div>
  );
}
